import { createSlice } from "@reduxjs/toolkit";
import { Address, AddressRepository, Loading } from "../domain";
import { ServiceLocater, Services } from "../service-locater";
import { AppThunk } from "./store";

type AddressesState = {
  addresses?: Address[];
  loadingAddresses: Loading;
  loadingNewAddress: Loading;
  loadingDeleteAddress: Loading;
  loadingEditAddress: Loading;
};

const initialState: AddressesState = {
  loadingAddresses: "idle",
  loadingNewAddress: "idle",
  loadingDeleteAddress: "idle",
  loadingEditAddress: "idle",
};

const addressesSlice = createSlice({
  name: "addresses",
  initialState,
  reducers: {
    addressesLoading(state) {
      state.addresses = undefined;
      state.loadingAddresses = "pending";
    },
    addressesLoaded(state, { payload }) {
      state.addresses = payload;
      state.loadingAddresses = "succeeded";
    },
    addressesFailed(state) {
      state.loadingAddresses = "failed";
    },
    newAddressLoading(state) {
      state.loadingNewAddress = "pending";
    },
    newAddressLoaded(state) {
      state.loadingNewAddress = "succeeded";
    },
    newAddressFailed(state) {
      state.loadingNewAddress = "failed";
    },
    newAddressReset(state) {
      state.loadingNewAddress = "idle";
    },
    deleteAddressLoading(state) {
      state.loadingDeleteAddress = "pending";
    },
    deleteAddressLoaded(state) {
      state.loadingDeleteAddress = "succeeded";
    },
    deleteAddressFailed(state) {
      state.loadingDeleteAddress = "failed";
    },
    deleteAddressReset(state) {
      state.loadingDeleteAddress = "idle";
    },

    //
    editAddressLoading(state) {
      state.loadingEditAddress = "pending";
    },
    editAddressLoaded(state) {
      state.loadingEditAddress = "succeeded";
    },
    editAddressFailed(state) {
      state.loadingEditAddress = "failed";
    },
    editAddressReset(state) {
      state.loadingEditAddress = "idle";
    },
  },
});

export default addressesSlice.reducer;

const {
  addressesLoading,
  addressesFailed,
  addressesLoaded,
  newAddressFailed,
  newAddressLoaded,
  newAddressLoading,
  newAddressReset,
  deleteAddressFailed,
  deleteAddressLoaded,
  deleteAddressLoading,
  deleteAddressReset,
  editAddressFailed,
  editAddressLoaded,
  editAddressLoading,
  editAddressReset,
} = addressesSlice.actions;

export const loadAddresses = (): AppThunk => async (dispatch) => {
  dispatch(addressesLoading());
  const res = await ServiceLocater.get<AddressRepository>(
    Services.AddressRepository
  ).get();
  res.fold(
    (_) => dispatch(addressesFailed()),
    (r) => dispatch(addressesLoaded(r))
  );
};

export const addNewAddress = (
  state: string,
  city: string,
  address: string,
  zipCode?: string
): AppThunk => async (dispatch) => {
  dispatch(newAddressLoading());
  const res = await ServiceLocater.get<AddressRepository>(
    Services.AddressRepository
  ).add(state, city, address, zipCode);
  res.fold(
    (_) => {
      dispatch(newAddressFailed());
    },
    (_) => {
      dispatch(newAddressLoaded());
      dispatch(newAddressReset());
    }
  );
};

export const editAddress = (
  id: number,
  state: string,
  city: string,
  address: string,
  zipCode?: string
): AppThunk => async (dispatch) => {
  dispatch(editAddressLoading());
  const res = await ServiceLocater.get<AddressRepository>(
    Services.AddressRepository
  ).edit(id, state, city, address, zipCode);
  res.fold(
    (_) => {
      dispatch(editAddressFailed());
    },
    (_) => {
      dispatch(editAddressLoaded());
      dispatch(editAddressReset());
    }
  );
};

export const deleteAddress = (id: number): AppThunk => async (dispatch) => {
  dispatch(deleteAddressLoading());
  const res = await ServiceLocater.get<AddressRepository>(
    Services.AddressRepository
  ).delete(id);
  res.fold(
    (_) => {
      dispatch(deleteAddressFailed());
    },
    (_) => {
      dispatch(deleteAddressLoaded());
      dispatch(deleteAddressReset());
    }
  );
};
